import { useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface BeforeAfterPair {
  before: string
  after: string
  alt?: string
}

interface BeforeAfterCarouselProps {
  pairs: BeforeAfterPair[]
  beforeLabel?: string
  afterLabel?: string
  className?: string
}

export function BeforeAfterCarousel({
  pairs,
  beforeLabel = 'Prima',
  afterLabel = 'Dopo',
  className,
}: BeforeAfterCarouselProps) {
  const [index, setIndex] = useState(0)
  const [position, setPosition] = useState(50)

  if (pairs.length === 0) return null

  const current = pairs[index]

  const goTo = (next: number) => {
    setIndex((next + pairs.length) % pairs.length)
    setPosition(50)
  }

  const prev = () => goTo(index - 1)
  const next = () => goTo(index + 1)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6 }}
      className={cn('relative', className)}
    >
      <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-ink-deep">
        {/* After image as base layer */}
        <img
          src={current.after}
          alt={`${current.alt || ''} ${afterLabel}`}
          className="absolute inset-0 w-full h-full object-cover select-none"
          loading="lazy"
          draggable={false}
        />

        {/* Before image clipped by slider position */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        >
          <img
            src={current.before}
            alt={`${current.alt || ''} ${beforeLabel}`}
            className="absolute inset-0 w-full h-full object-cover select-none"
            loading="lazy"
            draggable={false}
          />
        </div>

        {/* Labels */}
        <span className="absolute top-3 left-3 z-10 px-2 py-1 text-xs font-medium tracking-wide uppercase bg-ink-deep/70 text-cream rounded">
          {beforeLabel}
        </span>
        <span className="absolute top-3 right-3 z-10 px-2 py-1 text-xs font-medium tracking-wide uppercase bg-primary/90 text-ink rounded">
          {afterLabel}
        </span>

        {/* Divider line + handle */}
        <div
          className="absolute inset-y-0 w-px bg-cream/80 pointer-events-none z-10"
          style={{ left: `${position}%` }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-primary text-ink flex items-center justify-center shadow-lg">
            <div className="w-px h-3 bg-ink/40 mx-0.5" />
            <div className="w-px h-3 bg-ink/40 mx-0.5" />
          </div>
        </div>

        {/* Invisible range input for drag interaction */}
        <input
          type="range"
          min={0}
          max={100}
          value={position}
          onChange={(e) => setPosition(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20"
          aria-label={`${beforeLabel} / ${afterLabel}`}
        />
      </div>

      {/* Carousel controls */}
      <div className="flex items-center justify-between mt-4">
        <button
          type="button"
          onClick={prev}
          className="p-2 rounded-full bg-ink-deep/80 text-cream hover:bg-ink-deep transition-colors"
          aria-label="Immagine precedente"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex gap-2">
          {pairs.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              className={cn(
                'w-2 h-2 rounded-full transition-colors',
                i === index ? 'bg-primary' : 'bg-ink/30 hover:bg-ink/50'
              )}
              aria-label={`Vai all'immagine ${i + 1}`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={next}
          className="p-2 rounded-full bg-ink-deep/80 text-cream hover:bg-ink-deep transition-colors"
          aria-label="Immagine successiva"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  )
}
